/* eslint-disable @angular-eslint/prefer-standalone */
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { ErrorResponse } from '@app/core/models/commom/error-response';
import { CompanyRequest } from '@app/core/models/company/company-request';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { AppState } from '@app/state';
import { UserService } from '@core/services/http/user.service';
import { IdValue } from '@interfaces/a-organizar/idValue';
import { User } from '@interfaces/a-organizar/user';
import { CnpjResponse } from '@interfaces/cnpj/cnpj-reponse';
import { Partner } from '@interfaces/cnpj/partner';
import { CompanyPartner, CompanyRegistrationSource } from '@interfaces/common/company';
import { Option } from '@interfaces/common/option';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { CompanyRefreshService } from '@services/helpers/company-refresh.service';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { CompanyService } from '@services/http/company.service';
import { FinancialInstitutionService } from '@services/http/financial-institution.service';
import { OnboardingService } from '@services/http/onboarding.service';
import { nameof } from '@src/app/core/utils/nameof.utils';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { MenuItem } from 'primeng/api';
import { CompanyPartnerModalComponent } from './company-partner-modal/company-partner-modal.component';
import {
  NO_COMPANY_SECTOR,
  toCompanySectorPayload,
} from './company-sector-field/company-sector-field.component';
import { Observable } from 'rxjs';
import { finalize, first } from 'rxjs/operators';

@Component({
  selector: 'app-company-register',
  templateUrl: './company-register.component.html',
  styleUrls: ['./company-register.component.scss'],
  standalone: false,
})
export class CompanyRegisterComponent extends VaultBaseComponent implements OnInit {
  defaultLanguage: string = 'pt-BR';
  customerPermissions = Permissions.Company;
  /** Contratos de cliente (card 3643): o cartão Contratos aparece em edição e com `CLIENTCONTRACT_LIST`. */
  clientContractPermissions = Permissions.ClientContract;

  /** Nome da empresa para o texto da modal Gerar contrato (razão social ou nome fantasia). */
  get contractCompanyName(): string {
    return (
      this.companyForm.get(this.companyRegisterProps.corporateName)?.value ||
      this.companyForm.get(this.companyRegisterProps.tradeName)?.value ||
      ''
    );
  }

  /** Setor padronizado (card 3642): o campo Setor só aparece com `COMPANYSECTOR_LIST`. */
  companySectorPermissions = Permissions.CompanySector;
  breadcrumbs: MenuItem[] = [];
  companyId: string = '';
  sequentialId: string = '';
  /** Quem cadastrou a empresa (PBI 3576); `CogDataRoom` mostra a faixa com a origem do cadastro. */
  registrationSource: CompanyRegistrationSource | null = null;
  partners: CompanyPartner[] = [];

  @ViewChild('partnerModal') partnerModal!: CompanyPartnerModalComponent;
  selectedPartnerIndex: number = -1;

  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: this.t(this.tk.General.Dashboard),
  };
  userStatusOptions: Option[] = [
    { label: this.t(this.tk.User.Active), value: '1' },
    { label: this.t(this.tk.User.Inactive), value: '0' },
  ];
  selectedStatus: Option | null = {
    label: this.t(this.tk.User.Active),
    value: '1',
  };
  financialInstitutionsOptions: Option[] = [];

  public companyRegisterProps = nameof<CompanyRequest>();
  companyForm = this.formBuilder.group({
    [this.companyRegisterProps.id]: [{ value: '', disabled: true }],
    [this.companyRegisterProps.status]: [''],
    [this.companyRegisterProps.corporateName]: ['', [Validators.required]],
    [this.companyRegisterProps.cnpj]: ['', [Validators.required]],
    [this.companyRegisterProps.tradeName]: [''],
    [this.companyRegisterProps.surname]: [''],
    [this.companyRegisterProps.description]: [''],
    [this.companyRegisterProps.address]: [''],
    [this.companyRegisterProps.addressNumber]: [''],
    [this.companyRegisterProps.addressComplement]: [''],
    [this.companyRegisterProps.addressNeighborhood]: [''],
    [this.companyRegisterProps.zipCode]: [''],
    [this.companyRegisterProps.city]: [''],
    [this.companyRegisterProps.sector]: [''],
    [this.companyRegisterProps.companySectorId]: [NO_COMPANY_SECTOR as string | null],
    [this.companyRegisterProps.state]: [''],
    [this.companyRegisterProps.website]: [''],
    [this.companyRegisterProps.officerId]: [null as string | null],
    [this.companyRegisterProps.weightedAverageCost]: [null as number | null],
    [this.companyRegisterProps.emailSlug]: [''],
  });

  constructor(
    translateService: TranslateService,
    store: Store<AppState>,
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private utilsService: UtilsService,
    private companyService: CompanyService,
    private formErrorHandler: FormErrorHandlerService,
    private toast: ToastHelperService,
    private onboardingService: OnboardingService,
    private financialInstitutionService: FinancialInstitutionService,
    private userService: UserService,
    private changeDetectorRef: ChangeDetectorRef,
    private companyRefreshService: CompanyRefreshService
  ) {
    super(translateService, store);
    this.defaultLanguage = translateService.currentLang;
  }

  users: User[] = [];
  userEmails: string[] = [];
  showQuickAddDialog: boolean = false;
  officerOptions: Option[] = [];
  officerOptionsLoading = false;
  quickAddEmails: string = '';
  sendOnboardingEmail: boolean = false;

  userColumns: ColumnDefinition[] = [
    new ColumnDefinition({ field: 'fullName', header: this.translate(this.tk.General.Name) }),
    new ColumnDefinition({ field: 'email', header: this.translate(this.tk.General.Email) }),
    new ColumnDefinition({
      field: 'lastLogin',
      header: this.t(this.tk.User.LastLogin),
    }),
    new ColumnDefinition({
      field: 'hasPassword',
      header: this.t(this.tk.User.HasPassword),
      style: { 'text-align': 'center' },
    }),
  ];

  ngOnInit(): void {
    this.breadcrumbs = [
      { label: this.t(this.tk.General.Administration), routerLink: '/' },
      {
        label: 'Empresas',
        routerLink: '/dashboard/company/list',
      },
      {
        label: this.t(this.tk.General.Register),
        routerLink: '/dashboard/company/register',
      },
    ];

    this.getFinancialInstitutions();
    this.getVerifyCompanyId();
  }

  getCompanyDataFromCNPJ(cnpj: string): Observable<CnpjResponse | null> {
    return this.onboardingService.getCompanyDataFromCnpj(cnpj);
  }

  handleCnpjInput(event: IdValue) {
    if (event && event.value.toString().trim().length == 14) {
      this.fetchCompanyData(event.value as string);
    }
  }

  fetchCompanyData(cnpj: string | null = null): void {
    if (!cnpj) {
      cnpj = this.companyForm.get(this.companyRegisterProps.cnpj)?.value;
    }

    this.utilsService.setIsLoading(true);
    this.getCompanyDataFromCNPJ(cnpj as string).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          // Populating form fields with the response data
          this.companyForm.controls[this.companyRegisterProps.corporateName]?.setValue(
            response.name || ''
          );
          this.companyForm.controls[this.companyRegisterProps.tradeName]?.setValue(
            response.tradeName || ''
          );
          // Card 3643: cada parte do endereço no seu campo (o contrato de cliente usa as partes);
          // `address` fica só com o logradouro.
          const address = response.address;
          this.companyForm.controls[this.companyRegisterProps.address]?.setValue(
            address?.street || ''
          );
          this.companyForm.controls[this.companyRegisterProps.addressNumber]?.setValue(
            address?.number || ''
          );
          this.companyForm.controls[this.companyRegisterProps.addressComplement]?.setValue(
            address?.complement || ''
          );
          this.companyForm.controls[this.companyRegisterProps.addressNeighborhood]?.setValue(
            address?.neighborhood || ''
          );
          this.companyForm.controls[this.companyRegisterProps.zipCode]?.setValue(
            response.address.zipCode || ''
          );
          this.companyForm.controls[this.companyRegisterProps.city]?.setValue(
            response.address.city || ''
          );
          this.companyForm.controls[this.companyRegisterProps.state]?.setValue(
            response.address.state || ''
          );
          this.companyForm.controls[this.companyRegisterProps.website]?.setValue(
            response.email || ''
          );

          this.partners = this.mapParterns(response.detailedPartners?.filter(x => x.name) || []);
          this.checkVisibleColumns();
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.error(error);
        this.toast.showToastError(
          this.t(this.tk.General.RequestError),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }
  visibleColumns = {
    hasCountry: false,
    hasLegalRepresentative: false,
    hasLegalRepresentativeQualification: false,
  };

  private checkVisibleColumns(): void {
    this.visibleColumns = {
      hasCountry: this.partners.some(partner => !!partner.countryOfOrigin?.trim()),
      hasLegalRepresentative: this.partners.some(
        partner => !!partner.legalRepresentativeName?.trim()
      ),
      hasLegalRepresentativeQualification: this.partners.some(
        partner => !!partner.legalRepresentativeQualification?.trim()
      ),
    };
  }

  getFinancialInstitutions() {
    this.utilsService.setIsLoading(true);
    this.financialInstitutionService.getFinancialInstitutions().subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          this.financialInstitutionsOptions = (response?.data || []).map(item => {
            return {
              label: `${item.displayName} | ${item.compensationCode}`,
              value: item.id,
              data: item,
            };
          });
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.error(error);
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingFinancialInstitutions),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  getVerifyCompanyId(): void {
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.companyId = params['id'];
        this.utilsService.setIsLoading(true);
        this.companyService.getCompanyById(this.companyId).subscribe({
          next: response => {
            this.utilsService.setIsLoading(false);
            if (response) {
              const company = response;

              this.sequentialId = company?.sequentialId || '';
              // PBI 3576: empresa cadastrada pelo Cog mostra a faixa com a origem do cadastro.
              this.registrationSource = company?.registrationSource ?? null;
              this.companyForm.controls[this.companyRegisterProps.id].setValue(
                company?.sequentialId || company?.id || ''
              );
              this.companyForm.controls[this.companyRegisterProps.corporateName].setValue(
                company?.corporateName || ''
              );
              this.companyForm.controls[this.companyRegisterProps.cnpj].setValue(
                company?.cnpj || ''
              );
              this.companyForm.controls[this.companyRegisterProps.tradeName].setValue(
                company?.tradeName || ''
              );
              // Nome do e-mail do Data Room (card 3400): o carregado não conta como edição.
              this.companyForm.controls[this.companyRegisterProps.emailSlug].setValue(
                company?.emailSlug || ''
              );
              this.companyForm.controls[this.companyRegisterProps.emailSlug].markAsPristine();
              this.companyForm.controls[this.companyRegisterProps.address].setValue(
                company?.address || ''
              );
              this.companyForm.controls[this.companyRegisterProps.addressNumber].setValue(
                company?.addressNumber || ''
              );
              this.companyForm.controls[this.companyRegisterProps.addressComplement].setValue(
                company?.addressComplement || ''
              );
              this.companyForm.controls[this.companyRegisterProps.addressNeighborhood].setValue(
                company?.addressNeighborhood || ''
              );
              this.companyForm.controls[this.companyRegisterProps.zipCode].setValue(
                company?.zipCode || ''
              );
              this.companyForm.controls[this.companyRegisterProps.city].setValue(
                company?.city || ''
              );
              this.companyForm.controls[this.companyRegisterProps.state].setValue(
                company?.state || ''
              );
              this.companyForm.controls[this.companyRegisterProps.website].setValue(
                company?.website || ''
              );
              this.companyForm.controls[this.companyRegisterProps.sector].setValue(
                company?.sector || ''
              );
              this.companyForm.controls[this.companyRegisterProps.companySectorId].setValue(
                company?.companySectorId || NO_COMPANY_SECTOR
              );

              this.partners = company.partners;
              this.checkVisibleColumns();
              this.userEmails = company.userEmails || [];
              this.sendOnboardingEmail = company.sendOnboardingEmail || false;
              this.companyForm.controls[this.companyRegisterProps.officerId].setValue(
                company.officerId ?? null
              );
              this.companyForm.controls[this.companyRegisterProps.weightedAverageCost].setValue(
                company.weightedAverageCost ?? null
              );
              this.loadOfficerCandidates();
            }
          },
          error: error => {
            this.utilsService.setIsLoading(false);
            console.error(error);
            this.toast.showToastError(
              this.t(this.tk.Management.ErrorLoadingCompany),
              error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
            );
          },
        });

        this.userService
          .getUsersList({ PageSize: 100, CompanyId: this.companyId })
          .subscribe(users => {
            // Usar setTimeout para evitar ExpressionChangedAfterItHasBeenCheckedError
            setTimeout(() => {
              this.users = users?.data || [];
              this.changeDetectorRef.markForCheck();
            }, 0);
          });
      } else {
        this.loadOfficerCandidates();
      }
    });
  }

  loadOfficerCandidates(): void {
    // Evitar múltiplas chamadas simultâneas
    if (this.officerOptionsLoading) {
      return;
    }

    this.officerOptionsLoading = true;
    const request = this.companyId
      ? this.companyService.getOfficerCandidates(this.companyId)
      : this.companyService.getOfficerCandidatesByTenant();

    request
      .pipe(
        first(), // Garantir que complete após o primeiro valor
        finalize(() => {
          // Sempre finalizar o loading, independente de sucesso ou erro
          this.officerOptionsLoading = false;
          // Marcar para detecção de mudanças no próximo ciclo
          setTimeout(() => {
            this.changeDetectorRef.markForCheck();
          }, 0);
        })
      )
      .subscribe({
        next: res => {
          try {
            const list = Array.isArray(res)
              ? res
              : ((res != null && typeof res === 'object' && 'data' in res
                  ? (res as { data?: unknown }).data
                  : null) ?? []);
            const items = Array.isArray(list) ? list : [];
            // Usar setTimeout para evitar ExpressionChangedAfterItHasBeenCheckedError
            setTimeout(() => {
              this.officerOptions = [
                { label: this.t(this.tk.General.AllA) || 'Todos', value: '' },
                ...items.map(
                  (u: { id: string; firstName?: string; surName?: string; email?: string }) => ({
                    label: [u.firstName, u.surName].filter(Boolean).join(' ') || u.email || u.id,
                    value: u.id,
                  })
                ),
              ];
              this.changeDetectorRef.markForCheck();
            }, 0);
          } catch (error) {
            console.error('Error processing officer candidates:', error);
            setTimeout(() => {
              this.officerOptions = [];
              this.changeDetectorRef.markForCheck();
            }, 0);
          }
        },
        error: err => {
          console.error('Error loading officer candidates:', err);
          setTimeout(() => {
            this.officerOptions = [];
            this.changeDetectorRef.markForCheck();
          }, 0);
        },
      });
  }

  openQuickAdd(): void {
    this.quickAddEmails = '';
    this.showQuickAddDialog = true;
  }

  confirmQuickAdd(): void {
    if (!this.quickAddEmails.trim()) {
      this.showQuickAddDialog = false;
      return;
    }

    const emails = this.quickAddEmails
      .split(/[,;\n]/)
      .map(e => e.trim())
      .filter(e => e.length > 0);

    // Simple validation and duplicate check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    let addedCount = 0;

    emails.forEach(email => {
      if (emailRegex.test(email) && !this.userEmails.includes(email)) {
        // Add to list for backend
        this.userEmails.push(email);

        // Add to table for display (mock user)
        const mockUser = {
          id: `temp-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
          email: email,
          firstName: '',
          surName: '',
          // Add other required fields with dummy values if accessed by table
          permissions: [],
          roles: [],
        } as unknown as User;

        this.users.push(mockUser);
        addedCount++;
      }
    });

    if (addedCount > 0) {
      this.toast.showToastSuccess(
        this.t(this.tk.General.EverythingIsFine),
        `${addedCount} email(s) adicionado(s) à lista.`
      );
    } else {
      this.toast.showToastWarn(
        this.t(this.tk.General.Attention1),
        'Nenhum email novo válido encontrado.'
      );
    }

    this.showQuickAddDialog = false;
  }

  handleOptionChange(selectedOptions: Option | null, key: string): void {
    switch (key) {
      case 'selectedStatus':
        this.selectedStatus = selectedOptions;
        this.companyForm.controls[this.companyRegisterProps.status].setValue(
          selectedOptions?.value || ''
        );
        break;
    }
  }

  cancelRegister(): void {
    this.router.navigate(['dashboard/company/list']);
  }

  mapParterns(partners: Partner[]): CompanyPartner[] {
    return partners.map(
      partner =>
        ({
          id: null, // Será gerado pelo backend
          companyId: this.companyId || '', // Se for edição, usa o ID existente
          name: partner.name,
          qualification: partner.qualification,
          countryOfOrigin: partner.countryOfOrigin || '',
          legalRepresentativeName: partner.legalRepresentativeName || '',
          legalRepresentativeQualification: partner.legalRepresentativeQualification || '',
        }) as CompanyPartner
    );
  }

  /** Societário da empresa (PBI 3260): quadro de sócios e administração lidos do contrato social. */
  openCorporateCharter(): void {
    if (this.companyId) {
      this.router.navigate(['/dashboard/company', this.companyId, 'societario']);
    }
  }

  openAddPartner(): void {
    this.selectedPartnerIndex = -1;
    this.partnerModal.open(null, this.companyId);
  }

  openEditPartner(partner: CompanyPartner, index: number): void {
    this.selectedPartnerIndex = index;
    this.partnerModal.open({ ...partner }, this.companyId);
  }

  onPartnerConfirmed(partner: CompanyPartner): void {
    const updated = [...this.partners];
    if (this.selectedPartnerIndex >= 0) {
      updated[this.selectedPartnerIndex] = partner;
    } else {
      updated.push(partner);
    }
    this.partners = updated;
    this.checkVisibleColumns();
  }

  removePartner(index: number): void {
    this.partners = this.partners.filter((_, i) => i !== index);
    this.checkVisibleColumns();
    this.changeDetectorRef.detectChanges();
  }

  getPartnerMenuItems(partner: CompanyPartner, index: number): MenuItem[] {
    return [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        command: () => this.openEditPartner(partner, index),
      },
      { label: 'Remover', icon: 'pi pi-trash', command: () => this.removePartner(index) },
    ];
  }

  registerCompany(): void {
    if (this.companyForm.invalid) {
      this.companyForm.markAllAsTouched();
      return;
    }

    const formValue = this.companyForm.getRawValue() as unknown as CompanyRequest;

    // Incluir todos os campos necessários do formulário
    const requestData: CompanyRequest = {
      ...formValue,
      partners: this.partners,
      status: this.selectedStatus?.value || '1', // Assuming '1' is the default active status
      // Incluindo os campos que foram removidos anteriormente
      address: this.companyForm.get(this.companyRegisterProps.address)?.value || '',
      addressNumber: this.companyForm.get(this.companyRegisterProps.addressNumber)?.value || null,
      addressComplement:
        this.companyForm.get(this.companyRegisterProps.addressComplement)?.value || null,
      addressNeighborhood:
        this.companyForm.get(this.companyRegisterProps.addressNeighborhood)?.value || null,
      zipCode: this.companyForm.get(this.companyRegisterProps.zipCode)?.value || '',
      city: this.companyForm.get(this.companyRegisterProps.city)?.value || '',
      state: this.companyForm.get(this.companyRegisterProps.state)?.value || '',
      sector: this.companyForm.get(this.companyRegisterProps.sector)?.value || '',
      companySectorId: toCompanySectorPayload(
        this.companyForm.get(this.companyRegisterProps.companySectorId)?.value,
        !!this.companyId
      ),
      website: this.companyForm.get(this.companyRegisterProps.website)?.value || '',
      userEmails: this.userEmails,
      sendOnboardingEmail: this.sendOnboardingEmail,
      officerId: this.companyForm.get(this.companyRegisterProps.officerId)?.value || null,
      weightedAverageCost:
        this.companyForm.get(this.companyRegisterProps.weightedAverageCost)?.value ?? null,
    };

    if (this.companyId) {
      this.editCompany(requestData);
    } else {
      this.postCompany(requestData);
    }
  }

  postCompany(formValue: CompanyRequest): void {
    this.utilsService.setIsLoading(true);
    this.companyService.postCompany(formValue).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (this.showRejection(response, this.t(this.tk.Management.ErrorCreatingCompany))) {
          return;
        }
        this.toast.showToastSuccess(
          this.t(this.tk.Management.CompanyCreatedSuccessfully),
          response?.message || ''
        );

        this.companyRefreshService.triggerRefresh();
        this.router.navigate(['dashboard/company/list']);
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        if (error instanceof ErrorResponse) {
          this.formErrorHandler.applyFormErrors(this.companyForm, error);
        } else {
          console.log('postCompany error: ', error);
        }
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorCreatingCompany),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  editCompany(formValue: CompanyRequest): void {
    this.utilsService.setIsLoading(true);
    this.companyService.putCompany(formValue, this.companyId).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (this.showRejection(response, this.t(this.tk.Management.ErrorUpdatingCompany))) {
          return;
        }
        this.toast.showToastSuccess(
          this.t(this.tk.Management.CompanyUpdatedSuccessfully),
          response?.message || ''
        );
        if (response) {
          this.companyRefreshService.triggerRefresh();
          this.router.navigate(['dashboard/company/list']);
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        if (error instanceof ErrorResponse) {
          this.formErrorHandler.applyFormErrors(this.companyForm, error);
        } else {
          console.log('editCompany error: ', error);
        }
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorUpdatingCompany),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  /**
   * O backend devolve a validação recusada dentro de um 200 (`isValid: false` + `results`). Mostra a
   * primeira mensagem, marca o campo do nome do e-mail quando é dele e mantém a pessoa na tela.
   */
  private showRejection(response: unknown, title: string): boolean {
    const validation = response as {
      isValid?: boolean;
      results?: { field?: string | null; message?: string }[];
    } | null;
    if (validation?.isValid !== false) {
      return false;
    }
    const results = validation.results ?? [];
    const slugError = results.find(r => r.field === 'EmailSlug');
    if (slugError) {
      const control = this.companyForm.controls[this.companyRegisterProps.emailSlug];
      control.setErrors({ ...(control.errors ?? {}), emailSlug: slugError.message });
      control.markAsTouched();
    }
    this.toast.showToastError(
      title,
      results[0]?.message || 'Nada foi salvo. Confira os campos e tente de novo.'
    );
    return true;
  }

  goToUserList(): void {
    this.router.navigate(['/dashboard/user/list'], {
      queryParams: { companyId: this.companyId },
    });
  }
}
